import { Entity } from '@core/models/entity.model';

/** Daily Check-In — AIO-80387. */

export const DAILY_CHECKIN_DAY_COUNT = 7;

export enum DailyCheckInRewardType {
  /** A configured day that pays nothing. Its label and image are both optional. */
  None = 0,
  Credit = 1,
  Promo = 2,
  Prize = 3,
}

/**
 * A currency the grid can hold amounts for. Comes from GET /dailycheckin/settings, not the
 * shared /currency dropdown — that one is scoped to the BO user and carries no symbol.
 */
export interface DailyCheckInCurrency {
  id: number;
  code: string;
  symbol: string;
  ratio: number;
  /** Credit bounds for this currency, computed API-side by CheckInCreditAmountRange. */
  min_amount?: string;
  max_amount?: string;
}

/** The single global settings row (basic settings panel). */
export interface DailyCheckInSettings {
  id: number;
  status: number;
  /** 0 = continue progress, 1 = reset on miss, 2 = daily only. */
  reset_on_missed: 0 | 1 | 2;
  /**
   * Named `icon_path`, not `icon`, on purpose. The API's global checkSpecialCharacters
   * validator not_regex-rejects `/` on any top-level scalar and only whitelists `icon_path`,
   * so an S3 URL sent as `icon` would 422.
   */
  icon_path: string | null;
  /** Optional; null means the player site's default missed icon. Same whitelist as icon_path. */
  missed_icon_path: string | null;
}

/**
 * One day-cell of a set: Day 1..7 FOR ONE CURRENCY.
 *
 * The reward type is per currency, so Day 1 can be Credit in MYR and Prize in THB. Switching
 * the grid's currency selector rebinds the whole cell — type, amount, promo, prize and image
 * — not just the amount.
 */
export interface DailyCheckInSchedule {
  id?: number;
  day: number;
  settings_currency_id: number;
  reward_type: DailyCheckInRewardType;
  /** Credit cells only. Null/'' means this day pays nothing in this currency. */
  amount?: string | null;
  promotion_id?: number | null;
  prize_name?: string | null;
  image_path?: string | null;
  /** False when a promo cell points at a trashed or deactivated promotion. */
  promotion_available?: boolean;
  /**
   * What the promo picker binds to — angular2-multiselect works in item objects, not ids.
   * Transient: toPayload() sends promotion_id, never this.
   */
  promotion_selection?: any[];
  /**
   * Which of this cell's fields failed the last check — 'amount', 'image_path', 'promotion_id',
   * 'prize_name', 'reward_type'. Per FIELD rather than one flag per cell so the grid outlines
   * just the box at fault instead of every control in the column. Transient — never sent.
   */
  invalidFields?: string[];
  /**
   * Why the chosen promotion cannot run here — wrong currency, or a member group it does not
   * reach. Held beside the outline so the cell can say what is wrong without the operator
   * having to trigger a save. Null when the cell is fine. Transient — never sent.
   */
  promotionIssueText?: string | null;
}

export interface DailyCheckInSetMemberGroup {
  id?: number;
  member_group_id: number;
  settings_currency_id: number;
  member_group?: { id: number; name: string };
}

/** A set — the member groups sharing one 7-day schedule. */
export interface DailyCheckInSet extends Entity {
  name: string;
  status: number;
  member_groups?: DailyCheckInSetMemberGroup[];
  /** Write side. */
  /**
   * Write side, keyed by settings_currency_id. Member groups are per currency: the same group
   * can be run by one set in THB and another in MYR, so a flat list cannot express it.
   */
  member_group_ids_by_currency?: { [settingsCurrencyId: number]: number[] };
  schedules: DailyCheckInSchedule[];
  /** Set by the pre-save check when the name is blank. Transient — never sent. */
  nameInvalid?: boolean;
  /**
   * Set by the pre-save check when a currency the set schedules has no member group — the state
   * a duplicated set starts in, since groups are exclusive per currency and are never copied.
   * Transient — never sent.
   */
  groupsInvalid?: boolean;
  /**
   * Promotions offered to THIS set, narrowed to the currency on screen and the set's member
   * groups. Held on the set rather than a parallel array: the grid renders `visibleSets`, so
   * any index-keyed store silently pairs a row with another set's data. Transient — never sent.
   */
  promotionOptions?: any[];
  /** Ticked-but-not-yet-applied groups for this set's picker. Transient — never sent. */
  addGroupSelection?: any[];
}
